import { MenuItem, Paper } from '@mui/material';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { trackSaveEditCallOutcome } from 'src/common/userTracking';
import * as Yup from 'yup';
import { FormikTextField } from '../../../common/formik-material-ui';
import { FormikSelect } from '../../../common/formik-material-ui/FormikSelect';
import { user } from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { ModalWithStyles } from './ModalWithStyles';

export function ChangeDispositionModal({
  show,
  handleClose,
  order,
  dispoId,
  dispoName,
  dispoDestinationType,
  dispoDestinationId,
}) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  // const makeRequest = useMakePHPRequest({
  //   userAtom: userAtom.contents,
  // });
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const refetchToggleForManage = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_USER_DISPOSITION_REPORTING)
  );
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents;
  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtomContents?.businessid}`,
    })
  );
  const dispositionSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().max(100, 'Char limit 100').required('Required'),
        order: Yup.number().required('Required'),
        destinationType: Yup.string().required('Required'),
        destinationId: Yup.string().when('destinationType', {
          is: (dest) => dest === 'cadence' || dest === 'powerlist',
          then: Yup.string().required('Required'),
        }),
      }),
    []
  );
  const cadences = useMemo(
    () =>
      cadenceReportLoadable.state === 'hasValue'
        ? [
            ...(cadenceReportLoadable.contents?.response?.data ?? []),
          ]?.sort((a, b) =>
            a?.cadenceName
              ?.toLowerCase()
              ?.localeCompare(b?.cadenceName?.toLowerCase())
          ) ?? []
        : [],
    [cadenceReportLoadable]
  );
  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );
  const powerlists = useMemo(() => {
    if (powerlistReportLoadable.state === 'hasValue') {
      const pls = [
        ...(powerlistReportLoadable.contents?.response?.data?.powerlists ?? []),
      ];
      return (
        pls?.sort((a, b) =>
          a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
        ) ?? []
      );
    } else {
      return [];
    }
  }, [powerlistReportLoadable]);
  const onHandleClose = (formik) => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Paper square={true} elevation={0}>
      <Formik
        validationSchema={dispositionSchema}
        onSubmit={async (values, actions) => {
          const { name, order, destinationType, destinationId } = values;
          const data = {
            call: methodNameEnum.EDIT_DISPOSITION,
            apikey: userAtom.contents.apikey,
            businessid: userAtom.contents.businessid,
            order,
            disposition: name,
            dispositionid: dispoId,
            destinationType: destinationType,
            destinationId: destinationType === 'none' ? null : destinationId,
          };
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.EDIT_DISPOSITION,
            payload: data,
          });
          const resMessage = response?.data?.result?.code;
          refetchToggleForManage({});
          if (response?.data?.success) {
            enqueueSnackbarWithStyles({
              message: 'Successfully Edited Call Outcome',
              alertProps: { severity: 'success' },
            });
            handleClose();
          } else {
            enqueueSnackbarWithStyles({
              message:
                resMessage === 'ER_DUP_ENTRY'
                  ? 'Please choose a Call Outcome name that is not already in use'
                  : 'Encountered an error trying to edit call outcome',
              alertProps: { severity: 'error' },
            });
          }
          // handleClose();
          actions.resetForm({});
        }}
        // validationSchema={changeDispoSchema}
        enableReinitialize={true}
        initialValues={{
          name: dispoName,
          order: order,
          destinationType: dispoDestinationType ?? 'None',
          destinationId: dispoDestinationId,
        }}
      >
        {(formik) => (
          <ModalWithStyles
            onSubmit={() => {
              formik.handleSubmit();
              trackSaveEditCallOutcome();
            }}
            title="Edit Call Outcome"
            aria-labelledby="example-modal-sizes-title-md"
            show={show}
            onHide={() => onHandleClose(formik)}
            isSubmitting={formik.isSubmitting}
            isLoading={formik.isSubmitting}
            disableSubmit={!formik.dirty}
            size="md"
          >
            <div className="container my-5">
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Call Outcome Name</h5>
                  <p className="fieldDescription">
                    Enter a name for this call outcome. This is how the outcome
                    of the call or interaction will be labeled (e.g., ‘Not
                    Interested,’ ‘Left Voicemail’).
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikTextField
                    id="standard-basic"
                    label="Call Outcome Name"
                    name="name"
                    inputProps={{ maxLength: 100 }}
                    formik={formik}
                    size="small"
                    variant="outlined"
                    required
                    fullWidth
                  />
                </div>
              </div>
              <div className="row borderBottom">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Order</h5>
                  <p className="fieldDescription">
                    Set the priority order for this call outcome. Lower order
                    numbers are displayed first in the list.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikTextField
                    id="standard-basic"
                    label="Order"
                    name="order"
                    formik={formik}
                    size="small"
                    variant="outlined"
                    required
                    type="number"
                    fullWidth
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-8">
                  <h5 className="fieldTitle">Call Outcome Destination</h5>
                  <p className="fieldDescription">
                    Select where contacts should go after this call outcome is
                    applied. Note that this setting is ignored for calls
                    initiated by a Cadence or PowerList.
                  </p>
                </div>
                <div className="col-lg-4">
                  <FormikSelect
                    name="destinationType"
                    formik={formik}
                    fullWidth
                    label="Destination"
                    margin="dense"
                    variant="outlined"
                    required
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="cadence">Cadence</MenuItem>
                    <MenuItem value="powerlist">PowerList</MenuItem>
                  </FormikSelect>
                  <small className="fieldDescription">
                    Call Outcome Destination is ignored for calls initiated by a
                    Cadence or PowerList
                  </small>
                </div>
                {formik.values.destinationType === 'cadence' && (
                  <div className="subField">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Cadence</h5>
                      <p className="fieldDescription">
                        Select a Cadence to enroll the contact into after
                        applying this call outcome.
                      </p>
                    </div>
                    <div className="col-lg-4 pl-0">
                      {cadences.length > 0 ? (
                        <FormikSelect
                          name="destinationId"
                          formik={formik}
                          fullWidth
                          label="Cadence"
                          margin="dense"
                          variant="outlined"
                          required={formik.values.destinationType === 'cadence'}
                        >
                          {cadences.map((cadience, i) => {
                            if (cadience.cadenceIsActive) {
                              return (
                                <MenuItem key={i} value={cadience.id}>
                                  {cadience.cadenceName}
                                </MenuItem>
                              );
                            }
                          })}
                        </FormikSelect>
                      ) : (
                        <Link
                          to={`/manage/cadences/add`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Add Cadence
                        </Link>
                      )}
                    </div>
                  </div>
                )}
                {formik.values.destinationType === 'powerlist' && (
                  <div className="subField">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">PowerList</h5>
                      <p className="fieldDescription">
                        Select a PowerList to add the contact to after applying
                        this call outcome.
                      </p>
                    </div>
                    <div className="col-lg-4 pl-0">
                      {powerlists?.length > 0 ? (
                        <FormikSelect
                          name="destinationId"
                          formik={formik}
                          fullWidth
                          label="PowerList"
                          margin="dense"
                          variant="outlined"
                          required={
                            formik.values.destinationType === 'powerlists'
                          }
                        >
                          {powerlists.map((powerlist, index) => {
                            return (
                              <MenuItem key={index} value={powerlist.id}>
                                {powerlist.name}
                              </MenuItem>
                            );
                          })}
                        </FormikSelect>
                      ) : (
                        <Link
                          to={`/manage/powerlists/add`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Add PowerList
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ModalWithStyles>
        )}
      </Formik>
    </Paper>
  );
}
